import React, { Component, Suspense } from 'react';
import { endSession, sendWindowClosed } from '../../utils/analytics';
import { bugsnagClient } from '../../utils/bugsnag';

import MainHeaderBar from './MainHeaderBar';
import { Loader } from 'semantic-ui-react';

import { Switch, Route, Redirect } from 'react-router-dom';
import { FEATURE_LIST } from '../../permissions';

import { Authorization } from '../Auth';
import {
  PatchingV2,
  PatchingDetails,
  UtilizationOverview,
  LifeCycle,
  VpnActivity,
  ConfigChanges,
  FailedLogins,
  Users,
  Tickets,
  SupportOverview,
  NetworkThreats,
  TicketDetails,
  TechnologyRoadmap,
  SecurityAwarenessTraining,
  SecurityAwarenessTrainingDetails,
  InternetUtilization,
  InternetUtilizationDetails,
  SecurityIncidents,
  SecurityRemediations,
  Seats,
  DarkWebMonitoring,
  Vulnerabilities,
  VulnerabilitiesBasic,
  DNSActivity,
  SecurityPosture,
  ActiveDirectory,
  EndpointProtection,
  Backups,
  DevicesOverview,
  DevicesList,
  MyTickets,
  Reports,
  ClientSettings,
  Dashboard,
  OnboardingRoadmap,
  Documents,
} from '../';

//import Dashboard from '../Dashboard/Dashboard';

import {
  AutoLogout,
  Sidebar,
  ModalSecureAuth,
  EOLNotification,
  Feedback,
  HubSpotCTA,
  AutomatedNotification,
} from '../../components/';

import { AppContext } from '../../AppContext';

import { getToken, ErrorHandler } from '../../api';
import { jwtDecode } from '../../utils/jwt';
import { createEvent, eventTypes } from '../../utils/';

import isEqual from 'lodash/isEqual';

import './LayoutDefault.scss';
import { getLocalStorage } from '../../ManageCache';

class LayoutDefault extends Component {
  _isMounted = false;
  constructor(props) {
    super(props);
    //
    // If no token, kick 'em out.
    const token = getToken();
    const postLoginRedirect = props.location.pathname + props.location.search;

    // Prevent the component from trying to render before we are able to redirect
    let shouldComponentRender = false;

    const isNoToken =
      !token || typeof token === 'undefined' || token === 'undefined';

    if (isNoToken) {
      props.onRedirectToLogin(postLoginRedirect);
    } else {
      shouldComponentRender = true;
      const decoded = jwtDecode(token);
      const expiration = decoded.exp * 1000;
      const now = Date.now();
      // if the token expired
      if (expiration < now) {
        // go to login
        props.onRedirectToLogin(postLoginRedirect);
      }
    }

    this.state = {
      previousRoute: null,
      selectedUser: 1,
      shouldComponentRender,
    };
  }

  async componentDidMount() {
    this._isMounted = true;
    window.addEventListener('beforeunload', this.onUnload);

    this.props.history.listen(loc => {
      // Keep a short log of locations we are visiting within the site.
      // 1. initialize the global var to an empty array, or take a substring of <= 9 of that global array.
      // this way its never greater than 10 entries.
      window.locationHistory =
        window.locationHistory &&
        Array.isArray(window.locationHistory) &&
        window.locationHistory.length > 0
          ? window.locationHistory.slice(window.locationHistory.length - 9)
          : [];
      // Ensure that the route actually changed before sending an event.
      // If the query params are the only things that updated, we shouldn't send
      // a page load event.
      if (loc.pathname !== this.state.previousRoute) {
        this.safeSetState({
          previousRoute: loc.pathname,
        });
        window.locationHistory.push(loc.pathname);

        try {
          createEvent({
            route: loc.pathname,
            action: eventTypes.LOAD_PAGE,
            detail: loc.search,
          });
        } catch (err) {
          ErrorHandler.error(err);
        }
      }
    });
  }

  safeSetState = (newState, callback) => {
    if (this._isMounted) {
      this.setState(newState, callback);
    }
  };

  shouldComponentUpdate(nextProps, nextState) {
    // Lets update on any change other than location
    let didOnlyQueryParamsChange = false;
    const didLocationChange = !isEqual(this.props.location, nextProps.location);
    let didWeOnlyStripAnEmptyQueryParam = false;
    if (didLocationChange) {
      // so, location changed, lets see if only the
      // query params changed or the actual route
      const oldLoc = this.props.location.pathname;
      const newLoc = nextProps.location.pathname;
      // if only qp changed, then lets not update
      didOnlyQueryParamsChange = newLoc === oldLoc;

      // however lets check to see if we only stripped an empty qp
      // cause if so, then we might need to reload otherwise we get
      // a blank page.
      if (didOnlyQueryParamsChange) {
        // here we are looking for the original url search query having
        // a blank or empty value for a query param, something like this:
        // /devices/life-cycle?customer=23&SOMETHING
        // or
        // /devices/life-cycle?customer=23&SOMETHING=
        // because both of those will cause this to decide not to update,
        // which can cause the app to load a blank page. so we need to
        // force it to update in this situation.
        const search = this.props.location.search;
        const pairs = search.split('&');
        pairs.forEach(p => {
          if (!p) {
            return;
          }
          const pair = p.split('=');
          if (pair.length < 2 || pair[1] === '') {
            didWeOnlyStripAnEmptyQueryParam = true;
          }
        });
      }
    }

    let didAnythingOtherThanLocationChange = false;
    Object.keys(this.props).forEach(key => {
      if (key !== 'location' && key !== 'filters') {
        const same = isEqual(this.props[key], nextProps[key]);
        if (!same) {
          // uncomment this to find out who is causing updates
          // ErrorHandler.log(key, 'is who updated');
          didAnythingOtherThanLocationChange = true;
        }
      }
    });

    const didSelectedUserChange =
      this.state.selectedUser !== nextState.selectedUser;

    const areWeGoingToUpdate =
      didAnythingOtherThanLocationChange ||
      (didLocationChange && !didOnlyQueryParamsChange) ||
      didWeOnlyStripAnEmptyQueryParam ||
      didSelectedUserChange;

    /* // Toggle this on to test the loading logic
    ErrorHandler.log(
      { areWeGoingToUpdate },
      { didAnythingOtherThanLocationChange },
      { didLocationChange },
      { didOnlyQueryParamsChange },
      { didWeOnlyStripAnEmptyQueryParam },
      { didSelectedUserChange }
    );
     */

    return areWeGoingToUpdate;
  }

  componentWillUnmount() {
    this._isMounted = false;
    window.removeEventListener('beforeunload', this.onUnload);
  }

  onUnload = () => {
    endSession();
    sendWindowClosed();
  };

  handleUserSelect = (e, { value }) => {
    this.safeSetState({ selectedUser: value });
  };

  handleLogout = e => {
    e.preventDefault();
    this.props.logout();
  };

  render() {
    const { shouldComponentRender } = this.state;
    const { customerId, userProfile } = this.props;

    const { client_id, client_name, first_name, last_name, uuid } =
      userProfile || {};

    // Send the current logged in user's email to HubSpot
    const _hsq =
      typeof window !== 'undefined' && window._hsq ? window._hsq : [];
    if (userProfile && userProfile.email) {
      _hsq.push([
        'identify',
        {
          email: userProfile.email,
        },
      ]);
    }

    if (
      !userProfile ||
      !client_id ||
      !client_name ||
      !first_name ||
      !last_name
    ) {
      bugsnagClient.leaveBreadcrumb('User data is missing from userProfile', {
        userProfile: this.props.userProfile,
        localStorage: getLocalStorage(),
        client_id,
        client_name,
        first_name,
        last_name,
      });
    }

    return shouldComponentRender ? (
      <div className="LayoutDefault" id="LayoutDefault">
        <HubSpotCTA portalId="8677164" ctaId="155278424100" />
        <Feedback />
        <AutoLogout />
        <Sidebar />
        <ModalSecureAuth />
        <section className="app contentContainer">
          <AutomatedNotification />
          <EOLNotification />
          <MainHeaderBar
            userId={uuid}
            customerId={client_id}
            company={client_name}
            firstName={first_name}
            lastName={last_name}
            onLogout={this.handleLogout}
          />
          {customerId && (
            <div id="appContent" className="app content">
              <Suspense fallback={<Loader />}>
                <Switch>
                  <Route
                    path="/dashboard"
                    component={Authorization(FEATURE_LIST.DASHBOARD)(Dashboard)}
                  />
                  <Route
                    exact
                    path="/onboarding-roadmap"
                    component={Authorization(FEATURE_LIST.ONBOARDING_ROADMAP)(
                      OnboardingRoadmap
                    )}
                  />
                  <Route
                    exact
                    path="/my-tickets"
                    component={Authorization(FEATURE_LIST.MY_TICKETS)(
                      MyTickets
                    )}
                  />
                  <Route
                    exact
                    path="/documents"
                    component={Authorization(FEATURE_LIST.DOCUMENTS)(Documents)}
                  />
                  <Route
                    path="/security/incidents"
                    component={Authorization(FEATURE_LIST.SECURITY_INCIDENTS)(
                      SecurityIncidents
                    )}
                  />
                  <Route
                    path="/security/remediations"
                    component={Authorization(
                      FEATURE_LIST.SECURITY_REMEDIATIONS
                    )(SecurityRemediations)}
                  />
                  <Route
                    exact
                    path="/security/security-awareness-training"
                    component={Authorization(
                      FEATURE_LIST.SECURITY_AWARENESS_TRAINING
                    )(SecurityAwarenessTraining)}
                  />
                  <Route
                    path="/security/security-awareness-training/:user"
                    component={Authorization(
                      FEATURE_LIST.SECURITY_AWARENESS_TRAINING
                    )(SecurityAwarenessTrainingDetails)}
                  />
                  <Route
                    path="/security/dark-web-monitoring"
                    component={Authorization(FEATURE_LIST.DARK_WEB_MONITORING)(
                      DarkWebMonitoring
                    )}
                  />
                  <Route
                    path="/devices/overview"
                    component={Authorization(FEATURE_LIST.DEVICES_OVERVIEW)(
                      DevicesOverview
                    )}
                  />
                  <Route
                    path="/devices/list"
                    component={Authorization(FEATURE_LIST.DEVICES_LIST)(
                      DevicesList
                    )}
                  />
                  <Route
                    path="/devices/backups"
                    component={Authorization(FEATURE_LIST.BACKUPS)(Backups)}
                  />
                  <Route
                    path="/devices/seats"
                    component={Authorization(FEATURE_LIST.SEATS)(Seats)}
                  />
                  <Route
                    path="/devices/endpoint-protection"
                    component={Authorization(FEATURE_LIST.ENDPOINT_PROTECTION)(
                      EndpointProtection
                    )}
                  />
                  <Route
                    path="/devices/utilization"
                    component={Authorization(FEATURE_LIST.UTILIZATION)(
                      UtilizationOverview
                    )}
                  />
                  <Route
                    path="/devices/vulnerabilities"
                    component={Authorization(FEATURE_LIST.VULNERABILITIES)(
                      Vulnerabilities
                    )}
                  />
                  <Route
                    path="/devices/vulnerabilities-basic"
                    component={Authorization(
                      FEATURE_LIST.VULNERABILITIES_BASIC
                    )(VulnerabilitiesBasic)}
                  />
                  <Route
                    path="/devices/dns-activity"
                    component={Authorization(FEATURE_LIST.DNS_ACTIVITY)(
                      DNSActivity
                    )}
                  />
                  <Route
                    path="/devices/patching"
                    exact
                    component={Authorization(FEATURE_LIST.PATCHING_V2)(
                      PatchingV2
                    )}
                  />
                  <Route
                    path="/devices/patching/device/:id"
                    exact
                    component={Authorization(FEATURE_LIST.PATCHING_V2)(
                      PatchingDetails
                    )}
                  />
                  <Route
                    path="/devices/life-cycle"
                    component={Authorization(FEATURE_LIST.DEVICE_LIFE_CYCLE)(
                      LifeCycle
                    )}
                  />
                  <Route
                    path="/network-events/internet-utilization/24-hour"
                    exact
                    component={Authorization(FEATURE_LIST.INTERNET_UTILIZATION)(
                      InternetUtilization
                    )}
                    key="23"
                  />
                  <Route
                    path="/network-events/internet-utilization/30-day"
                    exact
                    component={Authorization(FEATURE_LIST.INTERNET_UTILIZATION)(
                      InternetUtilization
                    )}
                    key="23"
                  />
                  <Route
                    path="/network-events/internet-utilization/:date"
                    component={Authorization(FEATURE_LIST.INTERNET_UTILIZATION)(
                      InternetUtilizationDetails
                    )}
                  />
                  <Route
                    path="/network-events/network-threats"
                    component={Authorization(FEATURE_LIST.NETWORK_THREATS)(
                      NetworkThreats
                    )}
                  />
                  <Route
                    path="/network-events/vpn-activity"
                    component={Authorization(FEATURE_LIST.VPN_ACTIVITY)(
                      VpnActivity
                    )}
                  />
                  <Route
                    path="/network-events/configuration-changes"
                    component={Authorization(
                      FEATURE_LIST.CONFIGURATION_CHANGES
                    )(ConfigChanges)}
                  />
                  <Route
                    path="/network-events/failed-logins"
                    component={Authorization(FEATURE_LIST.FAILED_LOGINS)(
                      FailedLogins
                    )}
                  />
                  <Route
                    path="/my-organization/support-overview"
                    component={Authorization(FEATURE_LIST.SUPPORT_OVERVIEW)(
                      SupportOverview
                    )}
                  />
                  <Route
                    path="/my-organization/active-directory"
                    component={Authorization(FEATURE_LIST.ACTIVE_DIRECTORY)(
                      ActiveDirectory
                    )}
                  />
                  <Route
                    path="/admin/client-settings"
                    component={Authorization(FEATURE_LIST.CLIENT_SETTINGS)(
                      ClientSettings
                    )}
                  />
                  <Route
                    path="/admin/users"
                    component={Authorization(FEATURE_LIST.USER_MANAGEMENT)(
                      Users
                    )}
                  />
                  <Route
                    path="/my-organization/reports"
                    component={Authorization(FEATURE_LIST.REPORTS)(Reports)}
                  />
                  <Route
                    path="/my-organization/tickets"
                    exact
                    component={Authorization(FEATURE_LIST.TICKETS)(Tickets)}
                  />
                  <Route
                    path="/my-organization/tickets/:id"
                    component={Authorization(FEATURE_LIST.TICKETS)(
                      TicketDetails
                    )}
                  />
                  <Route
                    path="/my-tickets/:id"
                    component={Authorization(FEATURE_LIST.MY_TICKETS)(
                      TicketDetails
                    )}
                  />

                  <Route
                    path="/my-organization/technology-roadmap"
                    component={Authorization(FEATURE_LIST.TECH_ROADMAP)(
                      TechnologyRoadmap
                    )}
                  />
                  <Route
                    path="/my-organization/security-posture"
                    component={Authorization(FEATURE_LIST.SECURITY_POSTURE)(
                      SecurityPosture
                    )}
                  />
                  <Redirect
                    from="/my-organization/email-security"
                    to="/security/security-awareness-training"
                  />
                  <Redirect
                    from="/my-organization/email-security/:email"
                    to="/security/security-awareness-training/:user"
                  />
                  {/* this service changed, but we want to keep a redirect in case anyone had it bookmarked */}
                  <Redirect
                    from="/my-organization/compromises"
                    to="/security/dark-web-monitoring"
                  />
                  {/* this service changed, but we want to keep a redirect in case anyone had it bookmarked */}
                  <Redirect
                    from="/my-organization/compromises"
                    to="/security/dark-web-monitoring"
                  />
                  <Redirect
                    from="/network-events/internet-utilization"
                    to="/network-events/internet-utilization/24-hour"
                  />
                  <Redirect to="/dashboard" />
                </Switch>
              </Suspense>
            </div>
          )}
        </section>
      </div>
    ) : null;
  }
}

const WrappedLayoutDefault = props => (
  <AppContext.Consumer>
    {({
      userProfile,
      selectedCustomer,
      redirectToLoginWithRedirectAfter,
      logout,
      getUserProfile,
    }) => (
      <LayoutDefault
        {...props}
        getUserProfile={getUserProfile}
        userProfile={userProfile}
        customerId={selectedCustomer.id}
        onRedirectToLogin={redirectToLoginWithRedirectAfter}
        logout={logout}
      />
    )}
  </AppContext.Consumer>
);

export default WrappedLayoutDefault;
